import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import Aurora from '../Aurora/Aurora';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { clearPersistedLogin, loadPersistedLogin } from '../../lib/persistedAuth';
import './Hero.css';
// import hero1 from '../../assets/hero1.png';
// import hero2 from '../../assets/hero2.png';
// import hero3 from '../../assets/hero3.png';
// import hero4 from '../../assets/hero4.png';
// import hero5 from '../../assets/hero5.png';
// import hero6 from '../../assets/hero6.png';
import arrowDownAnimation from '../../assets/arrow_down.json';
import Logo from '../../assets/Logo.svg';

const Hero = () => {
  const [loaded, setLoaded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [remembered, setRemembered] = useState(null); // { userData, expiresAt }
  const [showQuickAccess, setShowQuickAccess] = useState(true); // Toggle between quick access and input
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    const id = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Load remembered teacher/student login for 1-click access.
  useEffect(() => {
    const saved = loadPersistedLogin();
    setRemembered(saved);
    // Show quick access if we have a remembered user, otherwise show input
    setShowQuickAccess(!!saved?.userData);
  }, []);

  // If we were redirected back here due to subscription gating, show the required message.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('error') === 'account-unavailable') {
      setError('Account Currently Unavailable');
    }
  }, [location.search]);

  // OIDC: when EC (or another partner) sends user here to log in, persist state so password step can resume flow
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oidcStateId = params.get('oidc_state_id');
    if (oidcStateId) {
      sessionStorage.setItem('oidc_state_id', oidcStateId);
    }
  }, [location.search]);

  // OIDC IdP-initiated: if user clicked Everybody Counts from /home they're already logged in; resume and redirect
  useEffect(() => {
    const oidcStateId = sessionStorage.getItem('oidc_state_id');
    if (!oidcStateId) return;
    const currentUserStr = sessionStorage.getItem('currentUser');
    if (!currentUserStr) return;
    let currentUser;
    try {
      currentUser = JSON.parse(currentUserStr);
    } catch {
      return;
    }
    if (!currentUser?.id) return;

    const apiBase = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!apiBase || !adminApiKey) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/oidc/resume-with-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': adminApiKey,
          },
          body: JSON.stringify({
            oidc_state_id: oidcStateId,
            user_id: currentUser.id,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        sessionStorage.removeItem('oidc_state_id');
        if (res.ok && data.redirect_uri) {
          window.location.href = data.redirect_uri;
        }
      } catch (e) {
        if (!cancelled) {
          console.error('OIDC resume-with-session failed', e);
          sessionStorage.removeItem('oidc_state_id');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);


  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError(''); // Clear error when user types
  };

  const validateUserId = (userId) => {
    // Validate user_id format (school code + number, e.g., ic3, abc123, school1)
    // Allows letters/numbers for school code + 1-3 digits
    const userRegex = /^[a-zA-Z0-9]+[0-9]{1,3}$/;
    return userRegex.test(userId);
  };

  const handleInputSubmit = async () => {
    const userId = inputValue.trim();

    if (!userId) {
      setError('Please enter a User ID');
      return;
    }

    // Secret admin access
    if (userId.toLowerCase() === 'admindashboard') {
      navigate('/dashboard');
      return;
    }

    // Secret school dashboard access
    if (userId.toLowerCase() === 'schooldashboard') {
      navigate('/mydashboard');
      return;
    }

    if (!validateUserId(userId)) {
      setError('Please enter a valid User ID');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Convert userId to lowercase for case-insensitive lookup (database stores user_id in lowercase)
      const normalizedUserId = userId.toLowerCase();// Fetch user profile with school information using user_id field
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          id, 
          user_id,
          first_name, 
          last_name, 
          full_name, 
          email, 
          role, 
          grade,
          grade_id,
          grade_section_id,
          teachers_grade_ids,
          is_active,
          school_id,
          schools!inner(
            id,
            name,
            school_id,
            contact,
            owner,
            subscription_start_date,
            subscription_end_date,
            subscription_duration_months,
            subscription_paid,
            subscription_warning_days
          )
        `)
        .eq('user_id', normalizedUserId)
        .single();

      if (userError) {if (userError.code === 'PGRST116') {
          throw new Error('User not found. Please check your User ID and try again.');
        }
        throw new Error(`User lookup failed: ${userError.message}`);
      }

      if (!userData) {
        throw new Error('User not found. Please check your User ID and try again.');
      }

      if (!userData.is_active) {
        throw new Error('Your account is inactive. Please contact your administrator.');
      }

      // Subscription gating (school-level)
      const school = userData.schools;
      const subscription = evaluateSchoolSubscription(school);
      if (!subscription.isAccessible) {
        // Required exact message
        throw new Error('Account Currently Unavailable');
      }// Store user data temporarily for password verification
      sessionStorage.setItem('pendingLogin', JSON.stringify({
        userData,
        attempts: 0
      }));

      // Navigate to password verification page
      navigate('/password-verification');

    } catch (err) {setError(err.message || 'Failed to validate user. Please check the User ID and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickContinue = () => {
    const saved = loadPersistedLogin();
    if (!saved?.userData) {
      setRemembered(null);
      return;
    }

    // Hydrate the same session key the rest of the app expects.
    sessionStorage.setItem('currentUser', JSON.stringify(saved.userData));
    navigate('/home');
  };

  const handleNotYou = () => {
    clearPersistedLogin();
    sessionStorage.removeItem('currentUser');
    setRemembered(null);
    setShowQuickAccess(false); // Hide quick access, show input
    setError('');
  };

  const displayName = remembered?.userData?.full_name
    || `${remembered?.userData?.first_name || ''} ${remembered?.userData?.last_name || ''}`.trim()
    || remembered?.userData?.user_id
    || 'User';

  const displayRole = String(remembered?.userData?.role || '').toLowerCase();
  const displaySchool = remembered?.userData?.schools?.name || '';


  return (
    <div className={`hero ${loaded ? 'loaded' : ''}`}>
      <div className="aurora-wrapper">
        <Aurora
          colorStops={["#0088FF", "#4ecdc4", "#1E40AF"]}
          blend={0.8}
          amplitude={1.2}
          speed={0.3}
        />
      </div>
      <div className="hero-container">
        <div className="hero-content">
          {/* Logo */}
          <div className="hero-logo">
            <img src={Logo} alt="MyDigitalSchool Logo" className="hero-logo-image" />
          </div>

          <h1 className="hero-title">Empowering Digital Learning</h1>
          <p className="hero-subtitle">Bringing schools, teachers, and students together with easy access to every learning resource</p>

          {/* Quick access for remembered teacher/student */}
          {remembered?.userData && showQuickAccess && (
            <div className="hero-quick-access" aria-label="Quick access">
              <div className="hero-quick-access-text">
                <div className="hero-quick-access-title">
                  Welcome back, <strong>{displayName}</strong>
                </div>
                <div className="hero-quick-access-subtitle">
                  {displayRole ? `${displayRole}` : 'teacher/student'}{displaySchool ? ` • ${displaySchool}` : ''}
                </div>
              </div>
              <div className="hero-quick-access-actions">
                <button
                  type="button"
                  className="hero-quick-access-btn"
                  onClick={handleQuickContinue}
                >
                  Home
                </button>
                <button
                  type="button"
                  className="hero-quick-access-link"
                  onClick={handleNotYou}
                >
                  Not you?
                </button>
              </div>
            </div>
          )}

          {/* User ID input - hidden when quick access is shown */}
          {!showQuickAccess && (
            <div className="hero-input-wrapper">
            <input
              className="hero-input"
              type="text"
              placeholder="Enter your User ID"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleInputSubmit();
                }
              }}
              disabled={isLoading}
            />
            <button
              className={`hero-input-btn ${inputValue.trim() ? 'has-content' : ''} ${isLoading ? 'loading' : ''}`}
              aria-label={isLoading ? 'Validating...' : 'Go'}
              onClick={handleInputSubmit}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleInputSubmit();
                }
              }}
            >
              {isLoading ? (
                <div className="hero-loading-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h12M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
          )}

          {/* Error message */}
          {error && (
            <div className="hero-error">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}
        </div>

        {/* Commented out student portraits section */}
        {/* <div className="hero-visuals">
          <div className="student-portraits">
            <div className="portrait-frame frame-yellow">
              <img src={hero1} alt="Two young girls" className="portrait-image" />
            </div>
            <div className="portrait-frame frame-purple">
              <img src={hero2} alt="Two young girls" className="portrait-image" />
            </div>
            <div className="portrait-frame frame-blue">
              <img src={hero3} alt="Teenage female student" className="portrait-image" />
            </div>
            <div className="portrait-frame frame-dark-blue">
              <img src={hero4} alt="Teenage male student" className="portrait-image" />
            </div>
            <div className="portrait-frame frame-red">
              <img src={hero5} alt="Adult male student" className="portrait-image" />
            </div>
            <div className="portrait-frame frame-green">
              <img src={hero6} alt="Young girl student" className="portrait-image" />
            </div>
          </div>
        </div> */}
        <div
          className="scroll-hint"
          onClick={() => window.enterContent?.()}
          aria-label="Scroll to next section"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              window.enterContent?.();
            }
          }}
        >
          <Lottie
            animationData={arrowDownAnimation}
            loop={true}
            autoplay={true}
            className="scroll-hint-lottie"
          />
        </div>
      </div>

    </div>
  );
};

export default Hero;
